import { FormattedMessage, useIntl } from 'react-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import jApi, { jArticleList, jCommentList } from 'jamespot-user-api';
import styled from 'styled-components';
import { Toast } from 'jamespot-front-business';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { DisplayForm } from '../../displayer/DisplayForm.component';
import { useAppDispatch } from '../../redux/store';
import { DisplayFormRef } from '../../displayer/types';
import { useAbortController } from '../../hooks';

const ModalForm = commonComponentsRegistry.getLazyComponent('ModalForm');
const Loader = commonComponentsRegistry.getLazyComponent('Loader');
const Icon = commonComponentsRegistry.getLazyComponent('Icon');
const Typography = commonComponentsRegistry.getLazyComponent('Typography');
const Ellipsis = commonComponentsRegistry.getLazyComponent('Ellipsis');
const StyledHref = commonComponentsRegistry.getLazyComponent('StyledHref');

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.md}px;
`;

const ContentPreview = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.space.md}px;
    align-items: center;
    border: 1px solid ${({ theme }) => theme.color.grey2};
    background-color: ${({ theme }) => theme.color.grey0};
    border-radius: ${({ theme }) => theme.size.sm}px;
    padding: ${({ theme }) => theme.space.md}px;
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.sm}px;
`;

export type AddReportModalProps = {
    onClose: () => void;
    articleId: number;
    articleUri: string;
    commentUri: string;
};

export const AddReportModal = ({ onClose, articleId, articleUri, commentUri }: AddReportModalProps) => {
    const intl = useIntl();
    const formRef = useRef<DisplayFormRef>(null);
    const dispatch = useAppDispatch();
    const [open, setOpen] = React.useState(true);
    const [article, setArticle] = useState<jArticleList>();
    const [comment, setComment] = useState<jCommentList>();
    const [contentLoading, setContentLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const abortController = useAbortController();

    useEffect(() => {
        Promise.all([
            (async () => {
                const res = await jApi.article.get<jArticleList>(
                    { idArticle: articleId, format: 'raw-list' },
                    { signal: abortController.signal },
                );
                setArticle(res.result);
            })(),
            (async () => {
                if (commentUri) {
                    const res = await jApi.article.getComments<jCommentList>(
                        { idArticle: articleId },
                        {
                            format: 'raw-list',
                            signal: abortController.signal,
                        },
                    );
                    setComment(res.result.find((c) => c.uri === commentUri));
                }
            })(),
        ]).then(() => {
            setContentLoading(false);
        });
    }, [abortController, articleId, commentUri]);

    const closeModal = useCallback(() => {
        setOpen(false);
        onClose();
    }, [onClose]);

    const submit = useCallback(
        (description?: string) => {
            if (!description?.trim()) {
                dispatch(
                    Toast.actions.error({ label: intl.formatMessage({ id: 'CRPT_Report_Description_Required' }) }),
                );
            } else {
                setSubmitLoading(true);
                jApi.contentReport
                    .createReport({
                        uri: commentUri || articleUri,
                        description: description,
                    })
                    .then(() => {
                        dispatch(Toast.actions.success({ label: intl.formatMessage({ id: 'CRPT_Report_Sent' }) }));
                        closeModal();
                    })
                    .catch((e) => {
                        Toast.actions.error({ label: e.errorMsg ?? 'GLOBAL_Error' });
                        setSubmitLoading(false);
                    });
            }
        },
        [articleUri, closeModal, commentUri, dispatch, intl],
    );

    const submitForm = useCallback(
        ({ description }: { description: string }) => {
            submit(description);
        },
        [submit],
    );

    return (
        <ModalForm
            title={intl.formatMessage({ id: 'CRPT_Add_Report' })}
            open={open}
            closeHandler={closeModal}
            onSubmit={async () => submit(formRef.current?.getValues().description)}
            enableClickAwayCloseModal
            maxHeight={'520px'}
            buttons={[
                {
                    children: <FormattedMessage id="GLOBAL_Cancel" />,
                    color: 'cancel',
                    clickHandler: closeModal,
                },
                {
                    children: <FormattedMessage id="GLOBAL_Send" />,
                    clickHandler: () => submit(formRef.current?.getValues().description),
                    color: 'valid',
                    loading: submitLoading,
                },
            ]}>
            {contentLoading ? (
                <Loader size={'l'} />
            ) : (
                <Container>
                    <ContentPreview>
                        <Icon name={commentUri ? 'icon-comment' : 'icon-file'} size={50} variant={'square'} />
                        <ContentWrapper>
                            <Typography variant={commentUri ? 'h3' : 'h2'}>
                                <Ellipsis label={article?.title} length={40} />
                            </Typography>
                            {comment && (
                                <Typography>
                                    {intl.formatMessage({ id: 'CRPT_Comment_Of' })}{' '}
                                    <StyledHref href={comment?._user._url}>{comment?._user.title}</StyledHref>
                                </Typography>
                            )}
                        </ContentWrapper>
                    </ContentPreview>
                    <DisplayForm ref={formRef} fields={['description']} type="contentReport" onSubmit={submitForm} />
                </Container>
            )}
        </ModalForm>
    );
};
